/**
 * DemoExamPage.tsx
 * V-SAT 2025 Demo Exam Page with support for all three exam types
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import DemoExam from '@/components/exam/DemoExam';
import ExamReview from '@/components/exam/ExamReview';
import type {
  DemoAnswer,
  DemoAnswerMap,
  DemoQuestion,
  DemoResultSummary,
} from '@/components/exam/types';
import { apiService } from '@vsat/shared/lib/api';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { useLazyQuestions } from '@vsat/shared/hooks';
import { mapLazyQuestionToDemoQuestion } from '@/utils/questionMapper';
import { toast } from 'sonner';
import { SEOHead } from '../components/SEOHead';

interface FlattenedExamData {
  info: {
    id?: string;
    title: string;
    subject?: string;
    duration: number;
    totalQuestions: number;
    totalPoints: number;
    description?: string;
    price?: number;
    passingScore?: number | null;
    standard?: string | null; // Exam standard: 'VSAT', 'THPT', or null
  };
  questions: DemoQuestion[];
}

const mapExamToDemoData = (exam: any): FlattenedExamData | null => {
  if (!exam || !Array.isArray(exam.clusters) || exam.clusters.length === 0) {
    return null;
  }

  const flattened: DemoQuestion[] = [];

  exam.clusters.forEach((cluster: any, clusterIndex: number) => {
    const clusterQuestions = cluster.clusterQuestions || [];
    clusterQuestions.forEach((question: any, questionIndex: number) => {
      if (!question) return;
      flattened.push({
        id: question.id,
        type: question.type,
        subject: (question.subject || exam.subject || 'english').toLowerCase(),
        stem: question.stem || question.context,
        category: question.category,
        points: question.points ?? 0,
        options: question.options || [],
        trueFalseItems: question.trueFalseItems || [],
        matchingPrompts: question.matchingPrompts || [],
        matchingOptions: question.matchingOptions || [],
        fillBlankAnswers:
          question.fillBlankAnswers && question.fillBlankAnswers.length > 0
            ? question.fillBlankAnswers.map((blank: any) =>
                typeof blank === 'string' ? blank : blank.blankId || blank.id || String(blank)
              )
            : question.type === 'short_text' || question.type === 'short'
              ? [`${question.id}-short`]
              : [],
        clusterId: cluster.clusterId || cluster.id,
        clusterContext: cluster.clusterContext
          ? {
              ...cluster.clusterContext,
              // Ensure stimulusData is included if it exists
              stimulusData: cluster.clusterContext.stimulusData,
            }
          : {
              clusterId: cluster.clusterId || cluster.id,
              title: cluster.title,
              content: cluster.description || '',
              subject: exam.subject,
              mediaType: 'text',
            },
        clusterOrder: clusterIndex + 1,
        clusterQuestionIndex: questionIndex,
        clusterQuestionCount: clusterQuestions.length,
        isClusterIntro: questionIndex === 0,
        questionClusterId: (question as any).questionClusterId || undefined, // Store questionClusterId for sub-clusters (from backend)
      });
    });
  });

  if (flattened.length === 0) {
    return null;
  }

  const totalPoints =
    exam.totalPoints ||
    exam.passingScore ||
    flattened.reduce((sum, question) => sum + (question.points || 0), 0);

  return {
    info: {
      id: exam.id,
      title: exam.title,
      subject: exam.subject,
      duration: exam.duration,
      totalQuestions: exam.totalQuestions || flattened.length,
      totalPoints,
      description: exam.description,
      price: exam.price ?? 0,
      passingScore: typeof exam.passingScore === 'number' ? exam.passingScore : null,
      standard: exam.standard || null, // Exam standard: 'VSAT', 'THPT', or null
    },
    questions: flattened,
  };
};

// function removed

export default function DemoExamPage() {
  const navigate = useNavigate();
  const { examId } = useParams<{ examId: string }>();
  const { isAuthenticated } = useAuthStore();
  const [showReview, setShowReview] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState<{
    resultSummary: DemoResultSummary;
    examAnswers: DemoAnswerMap;
    resultId?: string;
  } | null>(null);

  // Lazy loading hook - fetch questions progressively
  const {
    questions: lazyQuestionsMap,
    clusters: clustersMap,
    totalQuestions,
    sessionId: lazySessionId,
    timeRemaining,
    loading,
    fetchQuestionsAround,
    isQuestionLoaded,
    examInfo,
  } = useLazyQuestions({
    examId: examId || '',
    prefetchRange: 1, // Load only 3 questions at a time (prev, current, next)
    onSessionExpired: () => {
      toast.error('Phiên thi đã hết hạn');
      navigate('/login');
    },
  });

  // Get exam metadata from session (NOT questions)
  const examMetadata = useMemo(() => {
    // Priority 1: Use examInfo from backend (via useLazyQuestions)
    if (examInfo) {
      return examInfo;
    }
    return null;
  }, [examInfo]);

  // Convert lazy questions Map to DemoQuestion array
  const examData = useMemo(() => {
    if (!examMetadata) return null;

    const total = totalQuestions || examMetadata.totalQuestions || 0;
    // Create sparse/padded array to preserve indices and allow jumping to any question
    const questions = new Array(total).fill(null);

    // Populate loaded questions at correct indices
    Array.from(lazyQuestionsMap.entries()).forEach(([order, lazyQ]) => {
      // order is 1-based from backend
      if (order >= 1 && order <= total) {
        const cluster = lazyQ.clusterId ? (clustersMap.get(lazyQ.clusterId) as any) : undefined;
        questions[order - 1] = mapLazyQuestionToDemoQuestion(lazyQ, cluster);
      }
    });

    return {
      info: {
        id: examMetadata.id,
        title: examMetadata.title,
        subject: examMetadata.subject,
        duration: examMetadata.duration,
        totalQuestions: totalQuestions || examMetadata.totalQuestions,
        totalPoints: (totalQuestions || examMetadata.totalQuestions) * 6,
        price: examMetadata.price,
        passingScore: examMetadata.passingScore,
        standard: examMetadata.standard,
      },
      questions,
    };
  }, [lazyQuestionsMap, clustersMap, examMetadata, totalQuestions]);

  // Fetch initial questions on mount
  useEffect(() => {
    if (examId && !loading) {
      fetchQuestionsAround(1); // Start with first question
    }
  }, [examId]); // Only run on mount

  // Handle page change to fetch questions on demand
  const handlePageChange = useCallback(
    (index: number) => {
      // index is 0-based from DemoExam, convert to 1-based for fetch
      // fetchQuestionsAround handles fetching if not already loaded
      fetchQuestionsAround(index + 1);
    },
    [fetchQuestionsAround]
  );

  const resolveSelectedOptionId = (question: DemoQuestion, answer: DemoAnswer | undefined) => {
    if (!question.options || !answer) return null;
    const value = answer.value;

    if (typeof value === 'string') {
      const exactMatch = question.options.find(
        option => typeof option === 'object' && option !== null && option.id === value
      );
      if (exactMatch) {
        return value;
      }

      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        const option = question.options[numeric];
        if (!option) return null;
        return typeof option === 'string' ? null : option.id;
      }
    }

    if (typeof value === 'number') {
      const option = question.options[value];
      if (!option) return null;
      if (typeof option === 'string') {
        return null;
      }
      return option.id;
    }

    return null;
  };

  const handleSubmit = async (resultSummary: DemoResultSummary, examAnswers: DemoAnswerMap) => {
    console.log('handleSubmit called', { examData: !!examData, isAuthenticated, lazySessionId });

    if (!examData) {
      console.error('No examData available');
      toast.error('Không tìm thấy thông tin đề thi. Vui lòng thử lại.');
      return;
    }

    // Check if user is authenticated
    if (!isAuthenticated) {
      console.error('User not authenticated');
      toast.error('Vui lòng đăng nhập để nộp bài');
      navigate('/login');
      return;
    }

    // Submit to backend for server-side grading
    // Convert examAnswers to UserAnswerDto format for submission
    try {
      if (lazySessionId) {
        console.log('Submitting exam with sessionId:', lazySessionId);

        // Convert examAnswers to UserAnswerDto format
        const submitAnswers = Object.entries(examAnswers).map(([questionId, answer]) => {
          const dto: any = {
            questionId,
          };

          if (!answer || !answer.value) {
            return dto;
          }

          const value = answer.value;
          const question = examData.questions.find((q: any) => q.id === questionId);
          const questionType = question?.type || '';

          // Convert based on question type
          switch (questionType) {
            case 'mc':
            case 'mcq':
              if (Array.isArray(value)) {
                dto.selectedOptions = value;
              } else if (typeof value === 'string' || typeof value === 'number') {
                dto.selectedOption = String(value);
              }
              break;
            case 'tf':
            case 'tf_matrix':
            case 'trueFalse':
              if (typeof value === 'object' && value !== null) {
                dto.trueFalseAnswers = value;
              }
              break;
            case 'matching':
              if (typeof value === 'object' && value !== null) {
                dto.matchingAnswers = value;
              }
              break;
            case 'cloze':
            case 'fillBlank':
            case 'fill_blank':
              // CRITICAL: value is now a string (answer text) keyed by questionId, NOT blankId
              // For cluster questions, each question has its own answer stored as a string
              if (typeof value === 'string') {
                // Single answer - use questionId as key (backend will map to blankId)
                dto.fillAnswers = { [questionId]: value };
              } else if (typeof value === 'object' && value !== null) {
                // Object format - check if keys are questionIds or blankIds
                // If keys look like questionIds (long IDs), use as-is
                // If keys look like blankIds (blank0, blank1, etc.), convert to questionId
                const convertedFillAnswers: Record<string, string> = {};
                Object.entries(value).forEach(([key, val]) => {
                  // If key is a questionId (long ID format), use it directly
                  // If key is a blankId (blank0, blank1, etc.), we need to find the questionId
                  // For now, assume keys are already questionIds from EnhancedFillBlankQuestion
                  convertedFillAnswers[key] = String(val || '');
                });
                dto.fillAnswers = convertedFillAnswers;
              }
              break;
            case 'essay':
              if (typeof value === 'string') {
                dto.essayText = value;
              }
              break;
            case 'short':
            case 'short_text':
            case 'short_numeric':
              if (typeof value === 'string' || typeof value === 'number') {
                dto.selectedOption = String(value);
              }
              break;
          }

          return dto;
        });

        const submitResponse = await apiService.submitExam(examData.info.id || examId || '', {
          sessionId: lazySessionId,
          answers: submitAnswers, // Send all answers in payload
          timeSpent: resultSummary.durationSeconds,
        });

        console.log('Submit response:', submitResponse);

        // Store backend results (source of truth)
        const resultsData = {
          summary: {
            ...resultSummary,
            backendGrade: submitResponse.summary,
          },
          answers: examAnswers, // For review display
          examInfo: examData.info,
          questions: examData.questions, // For type checking in review
        };
        sessionStorage.setItem('examResults', JSON.stringify(resultsData));

        // Store AI grading ID if present (for polling in ExamResultsPage)
        const aiGradingId =
          submitResponse.aiGrading?.gradingId || submitResponse.summary?.aiGradingId;
        if (aiGradingId) {
          const resultId = submitResponse.summary?.resultId || submitResponse.resultId;
          if (resultId) {
            sessionStorage.setItem(`ai_grading_${resultId}`, aiGradingId);
          }
        }

        // Check if exam is free (price === 0 or undefined)
        const isFreeExam = !examData.info.price || examData.info.price === 0;

        // Show review page for all exams (mandatory for free exams, optional for premium)
        setPendingSubmission({
          resultSummary,
          examAnswers,
          resultId: submitResponse.summary?.resultId || submitResponse.resultId,
        });
        setShowReview(true);
        return;
      } else {
        console.error('No sessionId available');
        toast.error('Không tìm thấy phiên thi. Vui lòng thử lại.');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Lỗi nộp bài. Vui lòng thử lại.');
    }
  };

  const handleReviewSubmit = () => {
    // Review submitted, navigate to results
    setShowReview(false);
    const resultPath = pendingSubmission?.resultId
      ? `/ket-qua/${pendingSubmission.resultId}`
      : `/results/${examId}`;
    navigate(resultPath);
  };

  const handleReviewSkip = () => {
    // Skip review (should not happen for free exams, but handle gracefully)
    setShowReview(false);
    const resultPath = pendingSubmission?.resultId
      ? `/ket-qua/${pendingSubmission.resultId}`
      : `/results/${examId}`;
    navigate(resultPath);
  };

  const handleExit = () => {
    navigate('/kho-de-thi');
  };

  // Show review page if needed
  if (showReview && pendingSubmission && examData) {
    // Check if exam is free (price === 0 or undefined)
    const isFreeExam = !examData.info.price || examData.info.price === 0;

    return (
      <ExamReview
        examId={examId || ''}
        examTitle={examData.info.title}
        resultId={pendingSubmission.resultId}
        isRequired={isFreeExam} // Required only for free exams, optional for premium
        onSubmit={handleReviewSubmit}
        onSkip={isFreeExam ? undefined : handleReviewSkip} // Only allow skip for premium exams
      />
    );
  }

  // Show loading state while fetching headers/questions
  if (loading && (!examData || !examData.questions || examData.questions.length === 0)) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50 dark:from-gray-900 dark:via-emerald-900/20 dark:to-teal-900/20'>
        <SEOHead
          title='Làm bài thi | Hướng nghiệp CAT'
          description='Làm bài thi thử TN THPT trực tuyến.'
          noindex
        />
        <div className='flex flex-col items-center justify-center p-8'>
          <div className='w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4 shadow-lg shadow-emerald-500/25'></div>
          <p className='text-gray-600 dark:text-gray-400 font-medium animate-pulse'>
            Đang tải đề thi...
          </p>
        </div>
      </div>
    );
  }

  // Show error if exam not found or has no questions
  if (!examData || !examData.questions || examData.questions.length === 0) {
    const errorMessage = !examData ? 'Không tìm thấy đề thi' : 'Đề thi chưa có câu hỏi';

    const errorDetail = !examData
      ? 'Đề thi bạn đang tìm không tồn tại hoặc dữ liệu chưa được tải xuống.'
      : 'Đề thi này chưa có câu hỏi. Vui lòng quay lại trang chi tiết để thử lại.';

    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50 dark:from-gray-900 dark:via-emerald-900/20 dark:to-teal-900/20'>
        <div className='text-center max-w-md mx-auto p-8'>
          <div className='w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6'>
            <span className='text-3xl'>⚠️</span>
          </div>
          <h2 className='text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4'>
            {errorMessage}
          </h2>
          <p className='text-gray-600 dark:text-gray-400 mb-8'>{errorDetail}</p>
          <div className='flex flex-col gap-3'>
            <button
              className='px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold hover:shadow-lg hover:shadow-emerald-500/30 transition-all active:scale-95'
              onClick={() => navigate(`/thi-thu-thpt/${examId}`)}
            >
              Quay lại trang chi tiết
            </button>
            <button
              className='px-6 py-3 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-medium hover:bg-white dark:hover:bg-gray-800 transition-all'
              onClick={() => navigate('/thi-thu-thpt')}
            >
              Về danh sách đề thi
            </button>
          </div>

          {/* Debug Info */}
          {(import.meta.env.DEV || window.location.host.includes('stg')) && (
            <div className='mt-8 text-xs text-left text-gray-400 bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-auto max-h-40'>
              <p>ExamId: {examId}</p>
              <p>Has Data: {examData ? 'Yes' : 'No'}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${examData.info.title} | Hướng nghiệp CAT`}
        description='Làm bài thi thử TN THPT trực tuyến.'
        noindex
      />
      <DemoExam
        questions={examData.questions}
        durationMinutes={examData.info.duration}
        onSubmit={handleSubmit}
        onExit={handleExit}
        onPageChange={handlePageChange}
        examConfig={{
          id: examData.info.id || examId || 'demo-exam',
          title: examData.info.title,
          type: 'free',
          securityConfig: {
            tabRestriction: false,
            violationLimit: 99,
            restrictionLevel: 'none',
          },
          price: examData.info.price,
        }}
        examInfo={examData.info}
      />
    </>
  );
}
